import '../global.css';

import { initDatadog } from '@qogita/logging';
import { isServer, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { AppProps as NextAppProps } from 'next/app';
import Head from 'next/head';
import Script from 'next/script';
import React from 'react';

import { AppAuthChecker } from '#components/shared/AppAuthChecker';
import { ErrorBoundary } from '#components/shared/ErrorBoundary';
import { AuthenticationProvider } from '#contexts/Authentication';
import { NotificationProvider } from '#contexts/Notification';
import { UserProvider } from '#contexts/User';
import { DefaultErrorBoundaryFallback } from '#src/components/shared/ErrorBoundary/ErrorBoundary';
import { LaunchDarklyProvider } from '#src/contexts/LaunchDarkly';
import { queryClient } from '#src/contexts/QueryClient';
import { env } from '#src/env.mjs';
import { useAppAnalytics } from '#src/hooks/utils/useAppAnalytics';
import { Page } from '#src/types/next';

if (!isServer && env.NEXT_PUBLIC_DATADOG_RUM_ENABLED) {
  initDatadog({
    trackResources: true,
    allowedTracingUrls: [
      {
        match: `${window.location.protocol}//${window.location.host}`,
        propagatorTypes: ['datadog'],
      },
      {
        match: 'https://api.qogita.com',
        propagatorTypes: ['datadog'],
      },
      {
        match: 'https://api.test.qogita.com',
        propagatorTypes: ['datadog'],
      },
    ],
    beforeSend(event) {
      if (event.type === 'resource') {
        return (
          event.resource.type === 'fetch' &&
          event.resource.url.includes('https://api.qogita.com')
        );
      }
      return true;
    },
  });
}

type AppProps = NextAppProps & {
  Component: Page;
};

function TrustpilotScript() {
  return (
    <Script
      id="trustpilot-script-loader"
      src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
      strategy="lazyOnload"
    />
  );
}

/**
 * @description Sets up providers and analytics and basic meta tags
 */
function App({ Component, pageProps }: AppProps) {
  useAppAnalytics();

  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Qogita</title>
      </Head>

      <QueryClientProvider client={queryClient}>
        <AuthenticationProvider>
          <UserProvider>
            <ErrorBoundary fallback={<DefaultErrorBoundaryFallback />}>
              <NotificationProvider>
                <LaunchDarklyProvider>
                  <AppAuthChecker options={Component.options}>
                    <Component {...pageProps} />
                  </AppAuthChecker>
                </LaunchDarklyProvider>
              </NotificationProvider>
            </ErrorBoundary>
            <TrustpilotScript />
          </UserProvider>
        </AuthenticationProvider>

        <ReactQueryDevtools />
      </QueryClientProvider>
    </>
  );
}

export default App;
